/**
 * Main parent component for the home page
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { Container, Button } from 'reactstrap'
import LandingNav from './components/LandingNav'
import useMediaQuery from 'client/shared/js/hooks/useMediaQuery'
import DestinationsEmbed from './components/DestinationsEmbed'
import BoardInspirations from './components/BoardInspirations'
import CreateBoardForm from './components/CreateBoardForm'
import HotelBookings from './components/HotelBookings'
import { GoogleMapsApiWrapper } from 'client/shared/js/components/GoogleMaps'
import { HeroBanner } from 'client/shared/js/components/Hero'
import PopularDestinations from './components/PopularDestinations'
import Link from 'client/shared/js/components/Link'
import Footer from 'client/shared/js/components/Footer'
import { fetchAnnouncements } from 'client/shared/js/services/announcements/api'
import RouteMap from 'common/configs/routeMap'
import styles from './App.module.scss'

const ANNOUNCEMENT_PUBLISH_DAYS = 30
const YEAR = new Date().getFullYear()

const App = (props) => {
  const { maps } = props
  const [announcements, setAnnouncements] = useState([])
  const { isLaptop, isTablet } = useMediaQuery()

  useEffect(() => {
    // Fetch announcements from the last 30 days
    fetchAnnouncements(ANNOUNCEMENT_PUBLISH_DAYS).then((res) => {
      setAnnouncements(res.data)
    })
  }, [])

  return (
    <div className={cx(styles['landing'])}>
      <LandingNav
        className={cx(styles['navbar'])}
        maps={maps}
        container
        fluid />
      <Container fluid>
        <HeroBanner
          announcements={announcements}
          className={cx(styles['hero'])}
          photoUrl='/static/assets/img/backgrounds/hero/airplane-wing.jpg'
          size='lg'>
          <h1 className='font-d2 mb-3'>Build your perfect trip in minutes.</h1>
          <h2 className='font-h4 font-weight-regular'>
            Getting ready to travel? We can help with that.
          </h2>
          <CreateBoardForm className={styles['create-board-form']} btnText='Create your first trip' isNarrowWidth={isLaptop} />
        </HeroBanner>
      </Container>
      <Container fluid={isTablet}>
        <PopularDestinations className={cx(styles['section-padding-sm'])} />
        <BoardInspirations className={cx(styles['section-padding-sm'])} />
        <HeroBanner
          className={cx(styles['section-padding-sm'])}
          photoUrl='/static/assets/img/backgrounds/hero/map-pins.jpg'
          size='xs'>
          <h2 className='font-h3 font-weight-semibold'>It's {YEAR}, let's wait no more.</h2>
          <h3 className='font-h4'>It's about time you travel again. Where do you want to go?</h3>
          <CreateBoardForm className={styles['create-board-form']} />
        </HeroBanner>
        <HotelBookings className={cx(styles['section-padding-sm'])} />
        <HeroBanner
          className={cx(styles['section-padding-sm'])}
          photoUrl='/static/assets/img/backgrounds/hero/night-market.jpg'
          size='xs'>
          <h2 className='font-h3 font-weight-semibold'>Get curated content just for you.</h2>
          <h3 className='font-h4 my-2'>Curious to know your travel personality? We'll give you recommendations that match your style.</h3>
          <Link
            trackProps={{ 'Label': 'Landing Personality Type Button' }}
            to={`${RouteMap.quiz.url}${RouteMap.quiz.personality.url}`}
            anchor>
            <Button color='primary'>Take our quiz</Button>
          </Link>
        </HeroBanner>
        <DestinationsEmbed className={cx(styles['section-padding-sm'])} />
        <HeroBanner
          className={cx(styles['section-padding-sm'])}
          photoUrl='/static/assets/img/backgrounds/hero/traveler-backpack.jpg'
          size='xs'>
          <h2 className='font-h3 mb-2 font-weight-semibold'>We're excited to see where you go.</h2>
          <Link
            trackProps={{ 'Label': 'Landing Footer Plan Button' }}
            to={`${RouteMap.board.url}${RouteMap.board.new}`}
            anchor>
            <Button color='primary'>Build your trip now</Button>
          </Link>
        </HeroBanner>
      </Container>
      <Footer container />
    </div>
  )
}

App.propTypes = {
  maps: PropTypes.object
}

export default GoogleMapsApiWrapper(App)
